/**
 * apply-theme — runtime theme override loader.
 *
 * Při bootu aplikace načte z `GET /content/theme-colors` JSON s admin overrides
 * — barvy i zaoblení rohů (např.
 * `{ "bg-primary": "#0a0a0a", "accent": "#ff8800", "radius-control": "8px" }`) —
 * a injektuje je do `:root` jako CSS variable overrides skrze inline
 * `<style id="theme-override">`.
 *
 * Pokud endpoint vrátí 404 (žádné overrides ještě nejsou), nedělá nic —
 * defaults z `index.css` zůstávají platné.
 *
 * Storage: tabulka `content_pages` (key='theme-colors', html=JSON string).
 * Update přes admin UI `/admin/theme` → PATCH /content/theme-colors.
 */
import { THEME_COLORS, THEME_RADII } from "./theme.config";

const STYLE_ELEMENT_ID = "theme-override";
const VALID_NAMES = new Set(THEME_COLORS.map((c) => c.name));
/** Radius proměnné (`radius-control`, `radius-surface`) — viz theme.config. */
const VALID_RADIUS_NAMES = new Set(THEME_RADII.map((r) => r.name));
/** Povolená radius hodnota: 0–3 číslice + `px` (např. `8px`). */
const RADIUS_VALUE_RE = /^[0-9]{1,3}px$/;

interface ContentPageResponse {
  key: string;
  html: string;       // JSON string s overrides
  version: number;
}

/**
 * Mapping sémantická hex proměnná → shadcn HSL aliasy. `index.css` historicky
 * drží DVĚ paralelní sady proměnných:
 *   1) sémantické hex (`--accent`, `--bg-primary`, …) — viz `THEME_COLORS`
 *   2) shadcn HSL aliasy (`--primary`, `--background`, …) — pro Tailwind utility
 *
 * Theme editor mění jen #1, ale Tailwind utility na HP / scoreboard / … čtou #2
 * přes `hsl(var(--primary))`. Bez této propagace se admin change neprojeví
 * v žádném `bg-primary` / `text-primary` elementu (= většina veřejných stránek).
 */
const HSL_ALIASES: Record<string, readonly string[]> = {
  "bg-primary":   ["background"],
  "bg-card":      ["card", "popover"],
  "bg-secondary": ["secondary", "muted"],
  "bg-input":     ["input"],
  "text-primary": ["foreground", "card-foreground", "popover-foreground", "secondary-foreground"],
  "text-muted":   ["muted-foreground"],
  "text-on-accent": ["primary-foreground", "accent-foreground"],
  "accent":       ["primary", "ring"],
  "status-error": ["destructive"],
};

/**
 * Převede HEX (`#rrggbb` nebo `#rgb`) na shadcn HSL string formátu "H S% L%".
 * Vrací `null` pokud vstup není validní hex barva — alias se v tom případě
 * přeskočí (zůstane default z index.css).
 */
function hexToHslString(hex: string): string | null {
  const m = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  let h = m[1]!;
  if (h.length === 3) {
    h = h.split("").map((c) => c + c).join("");
  }
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0;
  let sat = 0;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
  }
  return `${Math.round(hue)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Aplikuje overrides na :root. Zpracuje barvy (z THEME_COLORS, vč. HSL aliasů
 * pro Tailwind utility) i radiusy (z THEME_RADII). Neznámé klíče ignoruje.
 */
export function applyThemeOverrides(overrides: Record<string, string>): void {
  // Validace + sanitizace — barvy a radiusy zvlášť.
  const safeColors: Record<string, string> = {};
  const safeRadii: Record<string, string> = {};
  for (const [name, value] of Object.entries(overrides)) {
    if (typeof value !== "string") continue;
    if (VALID_NAMES.has(name)) {
      // Basic sanity: hex / rgb()/hsl() / CSS color name. Hodnota nesmí
      // obsahovat `}`, `;`, `<`, `>` (obrana proti CSS injection).
      if (/[<>{};]/.test(value)) continue;
      if (value.length > 100) continue;
      safeColors[name] = value;
    } else if (VALID_RADIUS_NAMES.has(name)) {
      if (RADIUS_VALUE_RE.test(value)) safeRadii[name] = value;
    }
  }

  // Hex hodnoty zapíšeme přímo. Pro každou s aliasem v HSL_ALIASES zároveň
  // vyemitnme HSL variantu (protože Tailwind utility ji čtou přes hsl()).
  const lines: string[] = [];
  for (const [name, value] of Object.entries(safeColors)) {
    lines.push(`  --${name}: ${value};`);
    const aliases = HSL_ALIASES[name];
    if (aliases) {
      const hsl = hexToHslString(value);
      if (hsl) {
        for (const alias of aliases) {
          lines.push(`  --${alias}: ${hsl};`);
        }
      }
    }
  }
  // Radiusy — prostá injektáž `--radius-control` / `--radius-surface`.
  for (const [name, value] of Object.entries(safeRadii)) {
    lines.push(`  --${name}: ${value};`);
  }

  // Vytvoř nebo update <style id="theme-override"> na konci <head>
  let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = STYLE_ELEMENT_ID;
    document.head.appendChild(styleEl);
  }

  styleEl.textContent = `:root {\n${lines.join("\n")}\n}`;
}

/** Načte overrides z API a aplikuje. Idempotentní — bezpečné volat víckrát. */
export async function loadAndApplyTheme(): Promise<void> {
  try {
    const res = await fetch("/api/content/theme-colors");
    if (res.status === 404) {
      // Žádné overrides v DB — defaults z index.css stačí
      return;
    }
    if (!res.ok) {
      console.warn("[apply-theme] Nelze načíst theme overrides:", res.status);
      return;
    }
    const data = (await res.json()) as ContentPageResponse;
    let overrides: Record<string, string>;
    try {
      overrides = JSON.parse(data.html);
    } catch (e) {
      console.warn("[apply-theme] theme-colors není validní JSON:", e);
      return;
    }
    if (overrides && typeof overrides === "object") {
      applyThemeOverrides(overrides);
    }
  } catch (err) {
    console.warn("[apply-theme] Theme override fetch selhal:", err);
  }
}
